import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactPaginate from "react-paginate";
import Loading from "../ui/Loading";
import {
  fetchCmsNews,
  fetchLatestCmsNews,
  normalizeCmsPostsForLegacyView,
} from "../../api/cmsClient";
import { getCmsLocaleFromLanguage } from "../../api/cmsLocale";
import {
  cacheCmsLatestPosts,
  cacheCmsNewsPage,
  createFallbackNotice,
  fetchLocalNews,
  getCachedCmsLatestPosts,
  getCachedCmsNewsPage,
  getLocalNewsPage,
  shouldSuppressFallbackNotice,
} from "../../api/newsFallback";
import NewsFallbackNotice from "./NewsFallbackNotice";
import { mergeNewsPostsWithLocalMediaFallbacks } from "../../api/legacyMediaMerge";

const postsPerPage = 8;
const latestPostsLimit = 5;

function slugify(value = "") {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/gi, "dj")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");
}

function getNewsSlug(post) {
  return post?.attributes?.Slug || slugify(post?.attributes?.Title || "novost");
}

function getNewsPath(post) {
  if (post?.routePath) return post.routePath;
  return `/novosti/${post.id}-${getNewsSlug(post)}`;
}

function getPostImageCandidates(post) {
  const candidates = post?.attributes?.ImageCandidates?.length
    ? post.attributes.ImageCandidates
    : [post?.attributes?.Image];

  return [...candidates, "/assets/img/logo/fpepblog.png"].filter(Boolean);
}

function getPostImage(post) {
  return getPostImageCandidates(post)[0] || "/assets/img/logo/fpepblog.png";
}

function getPostExcerpt(post, maxLength = 125) {
  const description = post?.attributes?.Description || "";
  const normalized = String(description).replace(/\s+/g, " ").trim();

  if (normalized.length <= maxLength) return normalized;
  return `${normalized.substring(0, maxLength).trim()}...`;
}

function Novosti() {
  const [t, i18n] = useTranslation();
  const cmsLocale = getCmsLocaleFromLanguage(i18n.language);
  const isEnglish = cmsLocale === "en";
  const [allLocalPosts, setAllLocalPosts] = useState([]);
  const [currentPosts, setCurrentPosts] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fallbackActive, setFallbackActive] = useState(false);
  const [fallbackSource, setFallbackSource] = useState("cms");
  const [loadError, setLoadError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [source, setSource] = useState("cms");
  const [retrying, setRetrying] = useState(false);

  const applyLocalPostsPage = useCallback((posts, pageIndex = 0, reason = null) => {
    const localPage = getLocalNewsPage(posts, pageIndex, postsPerPage, latestPostsLimit);

    setAllLocalPosts(posts);
    setPageCount(localPage.pageCount);
    setRecentPosts(localPage.latestPosts);
    setCurrentPosts(localPage.posts);
    setFallbackActive(Boolean(reason));
    setFallbackSource("local");
    setSource("local");
    setLoadError(reason);
    setCurrentPage(pageIndex);
  }, []);

  const loadLocalFallback = useCallback(
    async (pageIndex = 0, reason = null) => {
      const localPosts = await fetchLocalNews();
      applyLocalPostsPage(localPosts, pageIndex, reason);
    },
    [applyLocalPostsPage],
  );

  const applyCachedPage = useCallback((cachedPage, pageIndex = 0, reason = null) => {
    const cachedLatest = cachedPage?.latestPosts?.length
      ? cachedPage.latestPosts
      : getCachedCmsLatestPosts(latestPostsLimit, cmsLocale);

    setCurrentPosts(cachedPage?.posts || []);
    setRecentPosts(cachedLatest || []);
    setPageCount(Number(cachedPage?.pageCount || 1));
    setFallbackActive(Boolean(reason));
    setFallbackSource("cache");
    setSource("cache");
    setLoadError(reason);
    setCurrentPage(pageIndex);
  }, [cmsLocale]);

  const loadCmsPage = useCallback(async (pageIndex = 0, signal) => {
    const page = pageIndex + 1;
    const [newsResult, latestResult, localPostsForMediaFallback] = await Promise.all([
      fetchCmsNews({ page, limit: postsPerPage, locale: cmsLocale, signal }),
      fetchLatestCmsNews({ limit: latestPostsLimit, locale: cmsLocale, signal }),
      fetchLocalNews().catch(() => []),
    ]);

    const cmsPosts = mergeNewsPostsWithLocalMediaFallbacks(
      normalizeCmsPostsForLegacyView(newsResult.data || []),
      localPostsForMediaFallback,
    );
    const cmsLatestPosts = mergeNewsPostsWithLocalMediaFallbacks(
      normalizeCmsPostsForLegacyView(latestResult.data || []),
      localPostsForMediaFallback,
    );
    const pagination = newsResult.meta?.pagination || {};
    const totalPages = Number(pagination.totalPages || 0);

    const payload = {
      posts: cmsPosts,
      latestPosts: cmsLatestPosts,
      pageCount: totalPages,
    };

    if (cmsPosts.length || cmsLatestPosts.length) {
      cacheCmsNewsPage(pageIndex, postsPerPage, payload, cmsLocale);
      cacheCmsLatestPosts(latestPostsLimit, cmsLatestPosts.length ? cmsLatestPosts : cmsPosts, cmsLocale);
    }

    return payload;
  }, [cmsLocale]);

  const applyCmsResult = useCallback((result, pageIndex = 0) => {
    setCurrentPosts(result.posts);
    setRecentPosts(result.latestPosts.length ? result.latestPosts : result.posts.slice(0, latestPostsLimit));
    setPageCount(result.pageCount);
    setFallbackActive(false);
    setFallbackSource("cms");
    setSource("cms");
    setLoadError(null);
    setCurrentPage(pageIndex);
  }, []);

  const applyBestFallback = useCallback(
    async (pageIndex = 0, error = null, reason = "unavailable") => {
      const shouldUseCache = reason !== "empty" && !shouldSuppressFallbackNotice(error);
      const cachedPage = shouldUseCache
        ? getCachedCmsNewsPage(pageIndex, postsPerPage, cmsLocale)
        : null;

      if (cachedPage?.posts?.length) {
        applyCachedPage(
          cachedPage,
          pageIndex,
          createFallbackNotice({ source: "cache", reason, context: "list" }),
        );
        return;
      }

      await loadLocalFallback(
        pageIndex,
        shouldSuppressFallbackNotice(error)
          ? null
          : createFallbackNotice({ source: "local", reason, context: "list" }),
      );
    },
    [applyCachedPage, loadLocalFallback, cmsLocale],
  );

  const loadInitialNews = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    try {
      const result = await loadCmsPage(0);

      if (!result.posts.length && result.pageCount === 0) {
        await applyBestFallback(0, null, "empty");
        return;
      }

      applyCmsResult(result, 0);
    } catch (error) {
      if (error?.name === "AbortError") return;

      console.warn("CMS news API unavailable. Applying news fallback system.", error);

      try {
        await applyBestFallback(0, error, "unavailable");
      } catch (fallbackError) {
        console.error("All news fallback sources failed:", fallbackError);
        setCurrentPosts([]);
        setRecentPosts([]);
        setPageCount(0);
        setFallbackActive(true);
        setFallbackSource("local");
        setLoadError("Novosti trenutno nijesu dostupne.");
      }
    } finally {
      setLoading(false);
    }

  }, [applyBestFallback, applyCmsResult, loadCmsPage]);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setLoadError(null);

      try {
        const result = await loadCmsPage(0, controller.signal);

        if (!isMounted) return;

        if (!result.posts.length && result.pageCount === 0) {
          await applyBestFallback(0, null, "empty");
          return;
        }

        applyCmsResult(result, 0);
      } catch (error) {
        if (!isMounted || error?.name === "AbortError") return;

        console.warn("CMS news API unavailable. Applying news fallback system.", error);

        try {
          await applyBestFallback(0, error, "unavailable");
        } catch (fallbackError) {
          if (!isMounted) return;
          console.error("All news fallback sources failed:", fallbackError);
          setCurrentPosts([]);
          setRecentPosts([]);
          setPageCount(0);
          setFallbackActive(true);
          setFallbackSource("local");
          setLoadError("Novosti trenutno nijesu dostupne.");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    load();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [applyBestFallback, applyCmsResult, loadCmsPage]);

  const handleRetry = async () => {
    setRetrying(true);

    try {
      await loadInitialNews();
    } finally {
      setRetrying(false);
    }
  };

  const handlePageClick = async ({ selected }) => {
    setCurrentPage(selected);

    if (source === "local") {
      if (allLocalPosts.length) {
        applyLocalPostsPage(allLocalPosts, selected, loadError);
        return;
      }

      try {
        await loadLocalFallback(selected, loadError);
      } catch (fallbackError) {
        console.error("Error fetching local posts:", fallbackError);
      }
      return;
    }

    setLoading(true);
    setLoadError(null);

    try {
      const result = await loadCmsPage(selected);
      applyCmsResult(result, selected);
    } catch (error) {
      console.warn("CMS news page request failed. Applying news fallback system.", error);

      try {
        await applyBestFallback(selected, error, "unavailable");
      } catch (fallbackError) {
        console.error("All news fallback sources failed:", fallbackError);
        setCurrentPosts([]);
        setPageCount(0);
        setFallbackActive(true);
        setFallbackSource("local");
        setLoadError("Novosti trenutno nijesu dostupne.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="inner-blog pt-120 pb-120">
      <div className="container">
        <div className="row">
          <div className="col-lg-8">
            {fallbackActive && loadError && (
              <NewsFallbackNotice
                message={loadError}
                source={fallbackSource}
                onRetry={handleRetry}
                retrying={retrying}
              />
            )}

            {loading ? (
              <Loading />
            ) : currentPosts.length ? (
              <div className="row fpep-news-list-grid">
                {currentPosts.map((post) => (
                  <div
                    className="col-md-6 mb-30 d-flex align-items-stretch"
                    key={`${post.source || "local"}-${post.cmsId || post.id}`}
                  >
                    <article className="fpep-home-news-card fpep-news-list-card hover-zoomin">
                      <Link to={getNewsPath(post)} className="fpep-home-news-card__thumb" aria-label={post.attributes.Title}>
                        <img
                          src={getPostImage(post)}
                          alt={post.attributes.Title || "FPEP novost"}
                          loading="lazy"
                          data-candidate-index="0"
                          onError={(event) => {
                            const candidates = getPostImageCandidates(post);
                            const currentIndex = Number(event.currentTarget.dataset.candidateIndex || 0);
                            const nextIndex = currentIndex + 1;
                            const nextCandidate = candidates[nextIndex] || "/assets/img/logo/fpepblog.png";

                            if (event.currentTarget.src !== nextCandidate) {
                              event.currentTarget.dataset.candidateIndex = String(nextIndex);
                              event.currentTarget.src = nextCandidate;
                            }
                          }}
                        />
                        {post.attributes.Date && (
                          <span className="fpep-home-news-card__date">
                            <i className="fas fa-calendar-alt" /> {post.attributes.Date}
                          </span>
                        )}
                      </Link>

                      <div className="fpep-home-news-card__body">
                        <h3>
                          <Link to={getNewsPath(post)}>{post.attributes.Title}</Link>
                        </h3>
                        <p>{getPostExcerpt(post)}</p>
                        <Link to={getNewsPath(post)} className="readmore fpep-home-news-card__readmore">
                          {isEnglish ? "Read more" : "Saznaj više"} <i className="fas fa-arrow-right" />
                        </Link>
                      </div>
                    </article>
                  </div>
                ))}
              </div>
            ) : (
              <div className="alert alert-info" role="status">
                {isEnglish ? "There are currently no published news posts." : "Trenutno nema objavljenih novosti."}
              </div>
            )}

            {pageCount > 1 && (
              <div className="pagination-wrap">
                <ReactPaginate
                  previousLabel={"<"}
                  nextLabel={">"}
                  breakLabel={"..."}
                  pageCount={pageCount}
                  forcePage={currentPage}
                  marginPagesDisplayed={2}
                  pageRangeDisplayed={3}
                  onPageChange={handlePageClick}
                  containerClassName={"pagination"}
                  activeClassName={"active"}
                  pageClassName={"page-item"}
                  pageLinkClassName={"page-link"}
                  previousClassName={"prev-link"}
                  previousLinkClassName={"prev-link"}
                  nextClassName={"next-link"}
                  nextLinkClassName={"next-link"}
                  breakClassName={"page-item"}
                  breakLinkClassName={"page-link"}
                />
              </div>
            )}
          </div>
          <div className="col-md-4 pt-10">
            <aside className="sidebar-widget">
              <section
                id="custom_html-5"
                className="widget_text widget widget_custom_html"
              >
                <h2 className="widget-title">{t("sidebar.follow")}</h2>
                <div className="textwidget custom-html-widget">
                  <div className="widget-social">
                    <a
                      href="https://www.facebook.com/fpebar.me"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fab fa-facebook-f" />
                    </a>
                    <a
                      href="https://www.instagram.com/fpepbar/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fab fa-instagram" />
                    </a>
                    <a
                      href="https://www.youtube.com/@fpepbar"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fab fa-youtube" />
                    </a>
                    <a
                      href="https://www.tiktok.com/@fpepbar"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Follow us on Tiktok"
                      title="Tiktok"
                    >
                      <i className="fab fa-tiktok" />
                    </a>
                    <a
                      href="https://www.linkedin.com/school/fpepbar/"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Follow us on LinkedIn"
                      title="LinkedIn"
                    >
                      <i className="fab fa-linkedin-in" />
                    </a>
                  </div>
                </div>
              </section>
              <section id="categories-1" className="widget widget_categories">
                <h2 className="widget-title">{t("sidebar.useful")}</h2>
                <ul>
                  <li className="cat-item cat-item-16">
                    <Link to="/o-fpep">{t("about.fpep")}</Link>
                  </li>
                  <li className="cat-item cat-item-23">
                    <Link to="/akreditacija-fpep">
                      {t("admission.Akreditacija")}
                    </Link>
                  </li>
                  <li className="cat-item cat-item-18">
                    <Link to="/upis">{t("upis.title")}</Link>
                  </li>
                  <li className="cat-item cat-item-22">
                    <Link to="/cijene-studija">{t("admission.Skolarina")}</Link>
                  </li>
                  <li className="cat-item cat-item-22">
                    <Link to="/erasmus">{t("saradnja.erasmus")}</Link>
                  </li>
                  <li className="cat-item cat-item-22">
                    <Link to="/projekti">{t("saradnja.projekti")}</Link>
                  </li>
                  <li className="cat-item cat-item-22">
                    <Link to="/dom-studenata">{t("studenti.dom")}</Link>
                  </li>
                  <li className="cat-item cat-item-22">
                    <Link to="/galerija">{t("navbar.Gallery")}</Link>
                  </li>
                  <li className="cat-item cat-item-22">
                    <Link to="/novosti">{t("navbar.News")}</Link>
                  </li>
                </ul>
              </section>
              <section
                id="recent-posts-4"
                className="widget widget_recent_entries"
              >
                <h2 className="widget-title">{t("sidebar.study")}</h2>
                <ul>
                  <li>
                    <span className="post-date">
                      <strong>{t("programs.osnovne")}</strong> <br />
                    </span>
                    <Link to="/racunovodstvo-finansije-i-bankarstvo">
                      {t("osnovne.rfb")}
                      <br />
                    </Link>
                  </li>
                  <li>
                    <Link to="/menadzment">
                      {t("osnovne.menadzment")}
                      <br />
                    </Link>
                  </li>
                  <li>
                    <Link to="/pravo-osnovne">
                      {t("osnovne.pravo")}
                      <br />
                    </Link>
                  </li>
                  <li>
                    <span className="post-date">
                      <strong>{t("programs.master")}</strong> <br />
                    </span>
                    <Link to="/poslovna-ekonomija">
                      {t("master.poslovnaEkonomija")}
                      <br />
                    </Link>
                  </li>
                  <li>
                    <Link to="/ekonomija-javnog-sektora">
                      {t("master.ekonomijaJavnogSektora")}
                      <br />
                    </Link>
                  </li>
                  <li>
                    <Link to="/pravo-master">
                      {t("master.pravo")}
                      <br />
                    </Link>
                  </li>

                  <li>
                    <span className="post-date">
                      <strong>{t("programs.specijalisticke")}</strong> <br />
                    </span>
                    <Link to="/menadzment-javnog-sektora-specijalisticke">
                      {t("specijalisticke.mjs")}
                      <br />
                    </Link>
                  </li>
                  <li>
                    <Link to="/menadzment-profitnog-sektora-specijalisticke">
                      {t("specijalisticke.mps")}
                      <br />
                    </Link>
                  </li>
                </ul>
              </section>
              <section
                id="recent-posts"
                className="widget widget_recent_entries"
              >
                <h2 className="widget-title">{isEnglish ? "Latest posts" : "Najnovije objave"}</h2>
                <ul>
                  {recentPosts.map((post) => (
                    <li key={`${post.source || "local"}-recent-${post.cmsId || post.id}`}>
                      <Link to={getNewsPath(post)}>
                        {post.attributes.Title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            </aside>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Novosti;
